import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';

import {
    useCurationConfigState,
    useCurationConfigValue,
    useSetCurationConfigState
} from '../../../../hooks/widgetConfiguration.js';

import RIQPillInput from '../../../../components/RIQPillInput.jsx';
import Selectize from '../../../../components/Selectize.jsx';

import QueryValidationError from '../QueryValidationError.jsx';

import useGetBrowserLanguage from '../../hooks/useGetBrowserLanguage.js';

import {
    WizardSectionPanel,
    WizardSectionField
} from './WizardSectionPanel.jsx';

import {
    useVerifySearch
} from '../../hooks/useVerifySearch.js';

import {
    listeningDefinitions
} from '../enums/listeningDefinitions.jsx';

import {
    RIQInfoPopover
} from '../../../../components/RIQPopover.jsx';

import SearchTips from './SearchTips.jsx';

const {
    defineQueryTitle,
    defineQueryDescription,
    languageTitle,
    languageDescription,
    primaryTermsTitle,
    primaryTermsTooltipTitle,
    primaryTermsDescription,
    primaryTermsOptionalIncludeTitle,
    primaryTermsOptionalIncludeDescription,
    primaryTermsOptionalIncludePrompt,
    primaryTermsOptionalExcludeTitle,
    primaryTermsOptionalExcludeDescription,
    primaryTermsOptionalExcludePrompt
} = listeningDefinitions['saved-search'];

const languageOptions = [{
    id: '__ALL__',
    name: 'All'
}].concat(_.values(RivalIQ.metrics.new.enums.ListeningLanguages));

export default function DefineQuery() {

    const [searchTerms, setSearchTerms] = useCurationConfigState('searchTerms');
    const [searchTermsAnd, setSearchTermsAnd] = useCurationConfigState('searchTermsAnd');
    const [searchTermsNot, setSearchTermsNot] = useCurationConfigState('searchTermsNot');

    const [language, setLanguage] = useCurationConfigState('language');
    const setSelectedLanguage = useSetCurationConfigState('selectedLanguage');
    const viewLanguage = useMemo(() => (language === null ? ['__ALL__'] : [language]), [language]);

    const {orderedErrors} = useVerifySearch(true, {
        searchTerms: searchTerms,
        searchTermsAnd: searchTermsAnd,
        searchTermsNot: searchTermsNot
    });

    const editingListeningQueryId = useCurationConfigValue('editingListeningQueryId');
    const [updatedLanguage, setUpdatedLanguage] = useState(false);

    const searchTermsError = !_.isEmpty(searchTerms) ? _.findLast(orderedErrors, {
        group: 'searchTerms',
        isFatal: true
    }) : null;
    const searchTermsWarning = !_.isEmpty(searchTerms) ? _.findLast(orderedErrors, {
        group: 'searchTerms',
        isFatal: false
    }) : null;
    const includeTermsError = _.findLast(orderedErrors, {
        group: 'searchTermsAnd.0',
        isFatal: true
    });
    const includeTermsWarning = _.findLast(orderedErrors, {
        group: 'searchTermsAnd.0',
        isFatal: false
    });
    const excludedTermsError = _.findLast(orderedErrors, {
        group: 'searchTermsNot',
        isFatal: true
    });
    const excludedTermsWarning = _.findLast(orderedErrors, {
        group: 'searchTermsNot',
        isFatal: false
    });

    const defaultLanguage = useGetBrowserLanguage();

    useEffect(() => {

        // I'd ideally not use useEffect here, but I can't update language directly in render due to https://github.com/facebookexperimental/Recoil/issues/12
        if (!editingListeningQueryId && _.isEmpty(language) && !searchTerms && !updatedLanguage) {
            setLanguage(defaultLanguage);
            setUpdatedLanguage(true);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function onSetPills(pills) {
        setSearchTerms(pills);
    }

    function onSetSearchTermsAndPills(pills) {
        if (_.isFunction(pills)) {
            pills = pills(searchTermsAnd?.[0] || []);
        }
        setSearchTermsAnd([pills]);
    }

    function onSetSearchTermsNotPills(pills) {
        setSearchTermsNot(pills);
    }

    const onLanguageOptionsSelected = useCallback((selectedId) => {
        setSelectedLanguage(selectedLanguage => {
            if (_.isEmpty(selectedId)) {
                return undefined;
            }

            if (selectedId === '__ALL__') {
                return null;
            }

            return selectedId;
        });
        setLanguage(language => {
            if (_.isEmpty(selectedId)) {
                return undefined;
            }

            if (selectedId === '__ALL__') {
                return null;
            }

            return selectedId;
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    return (

        <WizardSectionPanel
            title={defineQueryTitle}
            description={defineQueryDescription}
            className='tlw-gap-10'
        >
            <div className='tlw-mx-7.5'
            />

            <WizardSectionField
                prompt={
                    <div
                        className='tlw-flex tlw-justify-between'
                    >

                        <div>
                            {primaryTermsTitle}
                            <RIQInfoPopover
                                title={primaryTermsTooltipTitle}
                                description={primaryTermsDescription}
                            />
                        </div>
                        <SearchTips />
                    </div>
                }
            >
                <RIQPillInput
                    className='tlw-w-full'
                    initialFocusState={true}
                    inputUniqueId='primaryTerms'
                    pills={searchTerms || []}
                    setPills={onSetPills}
                    multiline={true}
                />
                <QueryValidationError
                    searchTermsError={searchTermsError}
                    searchTermsWarning={searchTermsWarning}
                />
            </WizardSectionField>

            <WizardSectionField
                prompt={primaryTermsOptionalIncludePrompt}
                description={primaryTermsOptionalIncludeDescription}
                title={primaryTermsOptionalIncludeTitle}
            >
                <RIQPillInput
                    className='tlw-w-full'
                    pills={searchTermsAnd?.[0] || []}
                    setPills={onSetSearchTermsAndPills}
                    focusedPillIdx={includeTermsError?.idx}
                    inputUniqueId='includeTermsInput'
                    multiline={true}
                />
                {includeTermsError &&
                    <div className='tlw-text-sm tlw-text-red-500'>
                        {includeTermsError.message}
                    </div>
                }
                {!includeTermsError && includeTermsWarning &&
                    <div>
                        <span className='fa fa-warning' />
                        <span className='tlw-px-2 tlw-text-sm'>
                            {includeTermsWarning.message}
                        </span>
                    </div>
                }
            </WizardSectionField>

            <WizardSectionField
                prompt={primaryTermsOptionalExcludePrompt}
                description={primaryTermsOptionalExcludeDescription}
                title={primaryTermsOptionalExcludeTitle}
            >
                <RIQPillInput
                    className='tlw-w-full'
                    pills={searchTermsNot || []}
                    setPills={onSetSearchTermsNotPills}
                    focusedPillIdx={excludedTermsError?.idx}
                    inputUniqueId='excludeTermsInput'
                    multiline={true}
                />
                {excludedTermsError &&
                    <div className='tlw-text-sm tlw-text-red-500'>
                        {excludedTermsError.message}
                    </div>
                }
                {!excludedTermsError && excludedTermsWarning &&
                    <div>
                        <span className='fa fa-warning' />
                        <span className='tlw-px-2 tlw-text-sm'>
                            {excludedTermsWarning.message}
                        </span>
                    </div>
                }
            </WizardSectionField>

            <div className='tlw-mx-7.5 tlw-h-0.5 tlw-bg-gray-600'
            />

            <WizardSectionField
                className='tlw-mb-12'
                prompt={languageTitle}
                description={languageDescription}
                title={languageTitle}
            >
                <Selectize
                    items={viewLanguage}
                    onChange={onLanguageOptionsSelected}
                    options={languageOptions}
                />
            </WizardSectionField>
        </WizardSectionPanel>
    );
}
